import { api } from "./api";
import {
  setAuthError,
  setAuthLoading,
  setToken,
  setUser,
} from "@/features/auth/authSlice";
import { DELETE, POST, PUT } from "./consts";
import { roleApi } from "./roleApi";
import { accountApi } from "./accountApi";
import { IVenueApiKey } from "@pulse-ui/codegen/generated_ts/authn";
import { IPortfolio } from "@pulse-ui/codegen/generated_ts/org_entity";

async function handlePulseJwtLogon(
  dispatch: any,
  queryFulfilled: Promise<{ data: string }>,
  errorMessage: string
) {
  try {
    dispatch(setAuthLoading(true));

    const token = await queryFulfilled;

    dispatch(setToken(token.data));
    localStorage.setItem("pulsejwt", token.data.replace("\n", ""));

    const userResult = await dispatch(accountApi.endpoints.getUser.initiate(undefined));

    if (userResult.data) {
      try {
        const userRolesResult = await dispatch(
          roleApi.endpoints.getUserRoles.initiate(userResult.data.user_id)
        );

        const roles = Array.isArray(userRolesResult.data) ? userRolesResult.data : [];
        const isAdmin = roles.includes("admin") && !userRolesResult.isError;

        dispatch(setUser({ ...userResult.data, admin: isAdmin, roles }));
      } catch {
        dispatch(setUser({ ...userResult.data, admin: false, roles: [] }));
      }
    }
  } catch {
    dispatch(setAuthError(errorMessage));
  } finally {
    dispatch(setAuthLoading(false));
  }
}

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAuthOptions: builder.query<
      {
        google_oauth2: Array<{
          display_name: string;
          google_client_id: string;
        }>;
        okta: Array<{
          display_name: string;
          client_id: string;
          verifier: string;
        }>;
        required: boolean;
      },
      void
    >({
      query: () => "/api/authn/v1/options",
    }),
    // --- GOOGLE AUTHENTICATION ---
    googleOAuthLogin: builder.mutation<
      string, // The API returns just the JWT token as text
      { credential: string; clientId: string }
    >({
      query: ({ credential, clientId }) => ({
        url: `/api/authn/v1/oauth2/${clientId}/logon`,
        method: POST,
        body: credential,
        responseHandler: (response: Response) => response.text(), // Handle text response
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handlePulseJwtLogon(dispatch, queryFulfilled, "Google authentication failed.");
      }
    }),

    // --- OKTA AUTHENTICATION ---
    oktaLogon: builder.mutation<
      string, // The API returns just the JWT token as text
      { code: string; codeVerifier: string; redirectUri: string; clientId: string }
    >({
      query: ({ code, codeVerifier, redirectUri, clientId }) => ({
        url: `/api/authn/v1/okta/${clientId}/logon`, // Adjust endpoint path as needed
        method: POST,
        body: {
          code,
          code_verifier: codeVerifier,
          redirect_uri: redirectUri,
        },
        responseHandler: (response: Response) => response.text(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handlePulseJwtLogon(dispatch, queryFulfilled, "Okta authentication failed.");
      }
    }),

    // --- API KEYS ---
    // Get API keys
    getApiKeys: builder.query<IVenueApiKey[], void>({
      query: () => `/api/auth/v1/key`,
      transformResponse: (response: any) => response?.keys || response?.data || [],
      providesTags: ["ApiKeys"],
    }),
    // Create API key
    createApiKey: builder.mutation<IVenueApiKey, IVenueApiKey>({
      query: (key: IVenueApiKey) => ({
        url: `/api/auth/v1/key`,
        method: POST,
        body: key,
      }),
      invalidatesTags: ["ApiKeys"],
    }),
    // Delete API key
    deleteApiKey: builder.mutation<void, string>({
      query: (key_id: string) => ({
        url: `/api/auth/v1/key/${key_id}`,
        method: DELETE,
      }),
      invalidatesTags: ["ApiKeys"],
    }),
    // --- AUTHZ ----
    // Check portfolio permissions
    checkPortfolioPermissions: builder.query<
      boolean,
      { user_id: string; portfolio_id: string }
    >({
      query: ({ user_id, portfolio_id }) =>
        `/api/authz/v1/${user_id}/portfolios/${portfolio_id}`,
    }),
    // Get user portfolios
    getUserPortfolios: builder.query<IPortfolio[], { user_id: string }>({
      query: ({ user_id }) => `/api/authz/v1/${user_id}/portfolios`,
      providesTags: ["UserPortfolios"],
    }),
    //Update user assigned portfolios
    updateUserAssignedPortfolios: builder.mutation<
      void,
      { user_id: string; portfolios: string[] }
    >({
      query: ({ user_id, portfolios }) => ({
        url: `/api/authz/v1/${user_id}/portfolios`,
        method: PUT,
        body: { portfolios },
      }),
      invalidatesTags: ["UserPortfolios"],
    }),
  }),
});

export const {
  useGetAuthOptionsQuery,
  useGoogleOAuthLoginMutation,
  useOktaLogonMutation,
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
  useCheckPortfolioPermissionsQuery,
  useGetUserPortfoliosQuery,
  useUpdateUserAssignedPortfoliosMutation,
} = authApi;
